import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    Divider,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Chip,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Business as BusinessIcon,
    Info as InfoIcon,
    Dns as DnsIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    PlayArrow as TestIcon,
    OpenInNew as OpenInNewIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';

interface OrganizationSettingsData {
    logoUrl?: string;
    domain?: string;
}

const OrganizationSettings: React.FC = () => {
    const theme = useTheme();
    const [settings, setSettings] = useState<OrganizationSettingsData>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [testingDomain, setTestingDomain] = useState(false);
    const [domainTestResult, setDomainTestResult] = useState<{
        success: boolean;
        message: string;
    } | null>(null);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('authToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/OrganizationSettings/GetSettings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao carregar configurações');
            }

            const data = await response.json();
            setSettings(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao carregar configurações');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validar tipo de arquivo
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
            if (!allowedTypes.includes(file.type)) {
                setError('Tipo de arquivo não permitido. Use JPG, PNG, GIF ou SVG.');
                return;
            }

            // Validar tamanho (máximo 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Arquivo muito grande. Tamanho máximo: 5MB.');
                return;
            }

            setSelectedFile(file);
            setError(null);

            // Criar preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewUrl(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const formData = new FormData();

            if (selectedFile) {
                formData.append('LogoFile', selectedFile);
            }

            if (settings.domain !== undefined) {
                formData.append('Domain', settings.domain);
            }

            const token = localStorage.getItem('authToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/OrganizationSettings/UpdateSettings`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Erro ao salvar configurações');
            }

            const data = await response.json();
            setSettings(data);
            setSelectedFile(null);
            setPreviewUrl(null);
            setSuccess('Configurações salvas com sucesso!');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao salvar configurações');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteLogo = async () => {
        try {
            setSaving(true);
            setError(null);

            const token = localStorage.getItem('authToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/OrganizationSettings/DeleteLogo`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao deletar logo');
            }

            setSettings(prev => ({ ...prev, logoUrl: undefined }));
            setDeleteDialogOpen(false);
            setSuccess('Logo removido com sucesso!');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao deletar logo');
        } finally {
            setSaving(false);
        }
    };

    const handleDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSettings(prev => ({ ...prev, domain: event.target.value }));
        // Limpar resultado do teste quando o domínio mudar
        setDomainTestResult(null);
    };

    const testDomain = async () => {
        if (!settings.domain) {
            setDomainTestResult({
                success: false,
                message: 'Digite um domínio para testar'
            });
            return;
        }

        try {
            setTestingDomain(true);
            setDomainTestResult(null);

            // Adicionar protocolo se não tiver
            let testUrl = settings.domain.trim();
            if (!testUrl.startsWith('http://') && !testUrl.startsWith('https://')) {
                testUrl = `https://${testUrl}`;
            }

            // Fazer requisição para o domínio
            const response = await fetch(testUrl, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                },
            });

            if (!response.ok) {
                throw new Error(`Erro HTTP: ${response.status} ${response.statusText}`);
            }

            const htmlContent = await response.text();

            // Verificar se contém o texto "You need to enable JavaScript to run this app." (case insensitive)
            const containsJavaScriptMessage = htmlContent.toLowerCase().includes('you need to enable javascript to run this app.');

            if (containsJavaScriptMessage) {
                setDomainTestResult({
                    success: true,
                    message: 'Domínio funcionando corretamente!'
                });
            } else {
                setDomainTestResult({
                    success: false,
                    message: 'Domínio não está funcionando corretamente'
                });
            }
        } catch (err) {
            setDomainTestResult({
                success: false,
                message: `Erro ao testar domínio: ${err instanceof Error ? err.message : 'Domínio não está respondendo'}`
            });
        } finally {
            setTestingDomain(false);
        }
    };

    const openDomainInNewTab = () => {
        if (!settings.domain) {
            setDomainTestResult({
                success: false,
                message: 'Digite um domínio para abrir'
            });
            return;
        }

        let url = settings.domain.trim();
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = `https://${url}`;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <BusinessIcon sx={{ fontSize: 32, mr: 2, color: theme.palette.primary.main }} />
                    <Typography variant="h4" component="h1">
                        Configurações do WhiteLabel
                    </Typography>
                </Box>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Configure o logo e domínio da sua empresa.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                {/* Seção do Logo */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Logo da Empresa
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                        <Avatar
                            src={previewUrl || settings.logoUrl}
                            sx={{ width: 80, height: 80 }}
                            variant="rounded"
                        >
                            <BusinessIcon sx={{ fontSize: 40 }} />
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="logo-upload"
                                type="file"
                                onChange={handleFileSelect}
                            />
                            <label htmlFor="logo-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadIcon />}
                                    sx={{ mr: 2 }}
                                >
                                    Selecionar Logo
                                </Button>
                            </label>

                            {settings.logoUrl && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={saving}
                                >
                                    Remover Logo
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        Formatos aceitos: JPG, PNG, GIF, SVG. Tamanho máximo: 5MB.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Seção do Domínio */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="primary" />
                        Domínio da Empresa
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Domínio"
                            placeholder="exemplo.com.br"
                            value={settings.domain || ''}
                            onChange={handleDomainChange}
                            helperText="Digite apenas o domínio (exemplo.com.br) sem http:// ou https://"
                            sx={{ maxWidth: 400 }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={testingDomain ? <CircularProgress size={16} /> : <TestIcon />}
                                onClick={testDomain}
                                disabled={testingDomain || !settings.domain}
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                {testingDomain ? 'Testando...' : 'Testar'}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<OpenInNewIcon />}
                                onClick={openDomainInNewTab}
                                disabled={!settings.domain}
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                Abrir
                            </Button>
                        </Box>
                    </Box>

                    {/* Resultado do Teste do Domínio */}
                    {domainTestResult && (
                        <Alert
                            severity={domainTestResult.success ? 'success' : 'error'}
                            sx={{ mb: 2, maxWidth: 600 }}
                            icon={domainTestResult.success ? <SuccessIcon /> : <ErrorIcon />}
                        >
                            {domainTestResult.message}
                        </Alert>
                    )}

                    {/* Card de Instruções DNS */}
                    <Card sx={{
                        bgcolor: theme.palette.mode === 'dark'
                            ? 'rgba(59, 130, 246, 0.15)'
                            : 'primary.light',
                        color: theme.palette.mode === 'dark'
                            ? theme.palette.text.primary
                            : 'primary.contrastText',
                        mb: 2,
                        border: theme.palette.mode === 'dark'
                            ? `1px solid ${theme.palette.primary.main}40`
                            : 'none'
                    }}>
                        <CardContent>
                            <Typography variant="h6" sx={{
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: theme.palette.mode === 'dark'
                                    ? theme.palette.text.primary
                                    : 'primary.contrastText'
                            }}>
                                <InfoIcon sx={{ color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.common.white }} />
                                Como ativar seu domínio
                            </Typography>
                            <Typography variant="body2" sx={{
                                mb: 2,
                                color: theme.palette.mode === 'dark'
                                    ? theme.palette.text.secondary
                                    : 'primary.contrastText'
                            }}>
                                Para ativar o seu domínio com a plataforma, siga os passos abaixo:
                            </Typography>

                            <Stepper orientation="vertical" sx={{ pl: 0 }}>
                                <Step active>
                                    <StepLabel
                                        StepIconComponent={() => (
                                            <DnsIcon sx={{
                                                color: theme.palette.mode === 'dark'
                                                    ? theme.palette.primary.main
                                                    : 'white',
                                                fontSize: 24
                                            }} />
                                        )}
                                    >
                                        <Typography variant="subtitle2" sx={{
                                            color: theme.palette.mode === 'dark'
                                                ? theme.palette.text.primary
                                                : 'white',
                                            fontWeight: 600
                                        }}>
                                            Configure o DNS
                                        </Typography>
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ pl: 2, pb: 2 }}>
                                            <Typography variant="body2" sx={{
                                                color: theme.palette.mode === 'dark'
                                                    ? theme.palette.text.secondary
                                                    : 'white',
                                                mb: 1
                                            }}>
                                                Configure o seu DNS para o tipo <strong>A</strong> com o valor:
                                            </Typography>
                                            <Chip
                                                label="138.197.225.13"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',

                                                    fontFamily: 'monospace',
                                                    bgcolor: theme.palette.mode === 'dark'
                                                        ? theme.palette.primary.main
                                                        : 'rgba(255, 255, 255, 0.2)',
                                                    color: theme.palette.mode === 'dark' ? '#000' : undefined,
                                                }}
                                            />
                                        </Box>
                                    </StepContent>
                                </Step>

                                <Step active>
                                    <StepLabel
                                        StepIconComponent={() => (
                                            <ScheduleIcon sx={{
                                                color: theme.palette.mode === 'dark'
                                                    ? theme.palette.primary.main
                                                    : 'white',
                                                fontSize: 24
                                            }} />
                                        )}
                                    >
                                        <Typography variant="subtitle2" sx={{
                                            color: theme.palette.mode === 'dark'
                                                ? theme.palette.text.primary
                                                : 'white',
                                            fontWeight: 600
                                        }}>
                                            Aguarde a propagação
                                        </Typography>
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ pl: 2, pb: 2 }}>
                                            <Typography variant="body2" sx={{
                                                color: theme.palette.mode === 'dark'
                                                    ? theme.palette.text.secondary
                                                    : 'white'
                                            }}>
                                                A configuração pode levar algumas horas para ser aplicada globalmente.
                                            </Typography>
                                        </Box>
                                    </StepContent>
                                </Step>

                                <Step active>
                                    <StepLabel
                                        StepIconComponent={() => (
                                            <CheckCircleIcon sx={{
                                                color: theme.palette.mode === 'dark'
                                                    ? theme.palette.success.main
                                                    : 'white',
                                                fontSize: 24
                                            }} />
                                        )}
                                    >
                                        <Typography variant="subtitle2" sx={{
                                            color: theme.palette.mode === 'dark'
                                                ? theme.palette.text.primary
                                                : 'white',
                                            fontWeight: 600
                                        }}>
                                            Domínio ativado
                                        </Typography>
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ pl: 2, pb: 2 }}>
                                            <Typography variant="body2" sx={{
                                                color: theme.palette.mode === 'dark'
                                                    ? theme.palette.text.secondary
                                                    : 'white'
                                            }}>
                                                Após a configuração, a aplicação estará com o seu domínio ativado.
                                            </Typography>
                                        </Box>
                                    </StepContent>
                                </Step>
                            </Stepper>
                        </CardContent>
                    </Card>
                </Box>

                {/* Botões de Ação */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || (!selectedFile && !settings.domain)}
                        sx={{ minWidth: 120, color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                    >
                        {saving ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Salvando...
                            </>
                        ) : (
                            'Salvar'
                        )}
                    </Button>
                </Box>
            </Paper>

            {/* Dialog de Confirmação para Deletar Logo */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Remoção</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja remover o logo da empresa?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDeleteLogo}
                        color="error"
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? 'Removendo...' : 'Remover'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrganizationSettings;
